// Module d'envoi unique des comptes rendus (intervention, ronde, gardiennage).
// Règle : un compte rendu ne quitte la file qu'une fois confirmé par le serveur, et aucun
// « échec » n'est affiché tant qu'on n'a pas vérifié que le serveur ne l'a PAS reçu.
import api from "@/lib/api";
import { formatApiError } from "@/lib/errors";

export const newSubmitId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`);

// File d'attente propre à chaque agent (un téléphone partagé n'envoie jamais le rapport d'un autre).
export const queueKey = (base, uid) => `${base}:${uid || "anon"}`;
export const readQ = (key) => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } };
export const writeQ = (key, q) => localStorage.setItem(key, JSON.stringify(q));
export function migrateQueue(base, uid) {
  if (!uid) return;
  const legacy = readQ(base);
  if (legacy.length) {
    const key = queueKey(base, uid); const cur = readQ(key);
    const ids = new Set(cur.map((x) => String(x.id)));
    writeQ(key, [...cur, ...legacy.filter((x) => !ids.has(String(x.id)))]);
  }
  localStorage.removeItem(base);
}

const AUTH_FLAG = "reports_auth_required";
export const setAuthRequired = (on) => {
  if (on) localStorage.setItem(AUTH_FLAG, "1"); else localStorage.removeItem(AUTH_FLAG);
  window.dispatchEvent(new Event("reports-auth-changed"));
};
export const isAuthRequired = () => localStorage.getItem(AUTH_FLAG) === "1";

// retry = réseau/délai/serveur ; auth = session à renouveler ; invalid = vraie erreur de saisie.
export function classify(err) {
  const s = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (!err?.response || s >= 500 || s === 408 || s === 429) return "retry";
  if (s === 409 && detail?.code === "SUBMISSION_IN_PROGRESS") return "retry";
  if (s === 401) return "auth";
  if (s === 403 && /connect|session/i.test(typeof detail === "string" ? detail : "")) return "auth";
  return "invalid";
}

// « Ce compte rendu est-il arrivé ? » — { intervention_id } si reçu, false sinon, null si impossible de vérifier.
export async function wasReceived(sid) {
  if (!sid) return null;
  try {
    const { data } = await api.get(`/reports/submission/${encodeURIComponent(sid)}`, { timeout: 15000 });
    return data?.received ? { intervention_id: data.intervention_id || "" } : false;
  } catch { return null; }
}

// Envoie puis, en cas d'erreur, vérifie la réception avant toute conclusion.
// Retourne { status: "sent" | "queued" | "auth" | "invalid", data, message, detail }.
export async function deliver(sid, send) {
  try {
    const { data } = await send();
    setAuthRequired(false);
    return { status: "sent", data };
  } catch (err) {
    const kind = classify(err);
    const detail = err?.response?.data?.detail;
    const rec = await wasReceived(sid);
    if (rec) { setAuthRequired(false); return { status: "sent", data: rec.intervention_id ? rec : null }; }
    if (kind === "invalid") return { status: "invalid", detail, message: formatApiError(detail) };
    if (kind === "auth") { setAuthRequired(true); return { status: "auth" }; }
    return { status: "queued" };
  }
}

export const PENDING_MSG = "Compte rendu conservé sur l'appareil — il partira automatiquement dès que possible.";
export const AUTH_MSG = "Session à renouveler — votre compte rendu est conservé. Reconnectez-vous pour l'envoyer.";
